// Profile.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

import ModalView from '../container/ModalView.js';
import ProfileInfoEditor from '../container/ProfileInfoEditor.js';
import UserProfileJsonLd from '../container/UserProfileJsonLd.js';
import PullToRefresh from '../container/PullToRefresh.js';
import ProfileTab from '../container/ProfileTab.js';
import ProfileLivestream from '../container/ProfileLivestream.js';

import UniversalContainer from '../component/UniversalContainer.jsx';
// import SeoHelmetWithTDK from '../component/SeoHelmetWithTDK.jsx';

import {
  headerHeight,
  footerNavigationHeight,
  color,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import media from '../style/media.js';

import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import { isClient, getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { SHOP_ID, ProductType } from '../resource/shopConstants.js';
import getProfileTdk from '../resource/getProfileTdk.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { getUserPicture, MediaAssetFormat } from '../resource/getMediaAsset.js';

export const INPUT_VERTICAL_PADDING = 12;

class Profile extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      intersectedFeedName,
      postIntersectedFeedName,
      userId,
      listType,
      defaultSubscriptionCurrency,
      isMeProfile,
      userDetailFetchStatus,
      isFirstPageFetched,
      filters,
      sorts,
      postFilters,
      postSorts,
      staticContext,
      fetchUser,
      fetchFeeds,
      fetchFeedsTotalCount,
      fetchDiamondPacks,
      fetchUserFollowersTotalCount,
      fetchUserFollowingTotalCount,
    } = props;
    if (staticContext.actions) {
      if ('error' === userDetailFetchStatus?.status) {
        staticContext.error = userDetailFetchStatus.error;
      }

      if (userId && !isMeProfile && !isFirstPageFetched) {
        staticContext.actions.push({
          function: fetchFeeds,
          argumentObject: {
            page: 1,
            type: intersectedFeedName,
            filters,
            sorts,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
        const shouldFetchPostCount = ProfileListType.POST !== listType;
        if (shouldFetchPostCount) {
          staticContext.actions.push({
            function: fetchFeedsTotalCount,
            argumentObject: {
              type: postIntersectedFeedName,
              filters: postFilters,
              sorts: postSorts,
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        }
        staticContext.actions.push({
          function: fetchUserFollowersTotalCount,
          argumentObject: {
            userId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
        staticContext.actions.push({
          function: fetchUserFollowingTotalCount,
          argumentObject: {
            userId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            userId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
        if (userId && defaultSubscriptionCurrency) {
          staticContext.actions.push({
            function: fetchDiamondPacks,
            argumentObject: {
              shopId: SHOP_ID.subscription,
              currency: defaultSubscriptionCurrency,
              type: ProductType.SUBSCRIPTIONS,
              productIds: [userId],
              httpProxyHeaders: staticContext.httpProxyHeaders,
            },
          });
        }
      }
    }
  }
  trackUserClick = ({ buttonId }) => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({
          pathname: window.location.pathname,
        }),
        buttonId,
      },
    });
  };
  handleClickBack = () => {
    const { goBack } = this.props;
    this.trackUserClick({
      buttonId: ButtonId.All.ButtonBack,
    });
    return goBack();
  };
  fetchUser = () => {
    const { userId, isMeProfile, fetchUser } = this.props;
    if (!isMeProfile && userId) {
      return fetchUser({ userId });
    }
    return;
  };
  fetchFollowStatus = () => {
    const {
      userId,
      meId,
      isMeProfile,
      fetchUserIsFollowing,
      fetchFollowersTotalCount,
      fetchFollowingTotalCount,
      fetchUserFollowersTotalCount,
      fetchUserFollowingTotalCount,
    } = this.props;
    if (meId && isMeProfile) {
      fetchFollowersTotalCount();
      return fetchFollowingTotalCount();
    }
    if (userId) {
      fetchUserIsFollowing({ userId });
      fetchUserFollowersTotalCount({ userId });
      return fetchUserFollowingTotalCount({ userId });
    }
    return;
  };
  fetchPosts = () => {
    const {
      intersectedFeedName,
      userId,
      meId,
      profileFeedUnixTimestamp,
      isMeProfile,
      filters,
      sorts,
      fetchFeeds,
    } = this.props;
    const usingUserId = meId && isMeProfile ? meId : userId;
    if (!usingUserId) {
      return;
    }
    return fetchFeeds({
      type: intersectedFeedName,
      filters,
      sorts,
      page: 1,
      shouldSkipLocalCache: true,
      unixTimestamp: profileFeedUnixTimestamp,
    });
  };
  fetchUserIsBlocked = () => {
    const { userId, isMeProfile, fetchUserIsBlocked } = this.props;
    if (userId && !isMeProfile) {
      return fetchUserIsBlocked({ userId });
    }
  };
  fetchShortLink = () => {
    const { shareLink, shortShareLink, fetchShortLink } = this.props;
    if (shareLink && !shortShareLink) {
      return fetchShortLink({ location: shareLink });
    }
  };
  fetchMeCreator = () => {
    const { meId, isMeProfile, fetchMeCreator } = this.props;
    if (meId && isMeProfile) {
      return fetchMeCreator();
    }
  };
  fetchDiamondPacks = () => {
    const {
      userId,
      defaultSubscriptionCurrency,
      isMeProfile,
      fetchDiamondPacks,
    } = this.props;
    if (userId && defaultSubscriptionCurrency && !isMeProfile) {
      return fetchDiamondPacks({
        shopId: SHOP_ID.subscription,
        currency: defaultSubscriptionCurrency,
        type: ProductType.SUBSCRIPTIONS,
        productIds: [userId],
      });
    }
    return;
  };
  fetchUserSubscription = () => {
    const { meId, userId, isMeProfile, fetchUserSubscription } = this.props;
    if (meId && userId && !isMeProfile) {
      return fetchUserSubscription({ userId });
    }
    return;
  };
  handleRefresh = async () => {
    const { isMeProfile, updateProfileFeedUnixTimestamp } = this.props;
    if (isMeProfile) {
      await updateProfileFeedUnixTimestamp({
        unixTimestamp: getCurrentUnixTimestamp(),
      });
    }
    return Promise.all([
      this.fetchUser(),
      this.fetchFollowStatus(),
      this.fetchUserIsBlocked(),
      this.fetchShortLink(),
      this.fetchPosts(),
      this.fetchMeCreator(),
      this.fetchUserSubscription(),
      this.fetchDiamondPacks(),
    ]);
  };
  subscribeOnlineStatus = () => {
    const { userId, isMeProfile, subscribeOnlineStatus } = this.props;
    if (!isMeProfile && userId) {
      return subscribeOnlineStatus({ userId, shouldRetry: true });
    }
  };
  renderProfileInfo = () => {
    const { userId, isMeProfile } = this.props;
    if (isMeProfile) {
      return (
        <ProfileInfoWrapper>
          <UniversalContainer filename="MeProfileInfo" isMeProfile />
        </ProfileInfoWrapper>
      );
    }
    if (userId) {
      return (
        <ProfileInfoWrapper>
          <UniversalContainer filename="UserProfileInfo" userId={userId} />
        </ProfileInfoWrapper>
      );
    }
    return null;
  };
  renderTabContent = () => {
    const { userId, listType, isMeProfile } = this.props;
    if (isMeProfile) {
      switch (listType) {
        case ProfileListType.LIVESTREAM_SESSION: {
          return <ProfileLivestream listType={listType} isMeProfile />;
        }
        default: {
          return (
            <>
              <ListWrapper>
                <UniversalContainer
                  filename="MeProfilePost"
                  listType={listType}
                  isMeProfile
                />
              </ListWrapper>
            </>
          );
        }
      }
    }
    return (
      <ListWrapper>
        <UniversalContainer
          filename="UserProfilePost"
          userId={userId}
          listType={listType}
        />
      </ListWrapper>
    );
  };
  renderHelmet() {
    const { userId, meId, username, biography, listType, staticContext, t } =
      this.props;
    const ssrHostname = staticContext?.requestHeaders?.hostname || 'swag.live';
    const hostname = !isClient ? ssrHostname : window.location.hostname;
    const { displayTitle, displayDescription, displayKeywords } = getProfileTdk(
      { username, biography, listType, t }
    );
    const userPicture = getUserPicture({
      userId,
      meId,
      size: 512,
      format: MediaAssetFormat.JPG,
    });
    return (
      <>
        <Helmet>
          <title>{displayTitle}</title>
          <meta name="description" content={displayDescription} />
          <meta name="keywords" content={displayKeywords} />
          <meta
            property="og:url"
            content={`https://${hostname}/u/${username}`}
          />
          <meta property="og:title" content={displayTitle} />
          <meta property="og:description" content={displayDescription} />
          <meta property="og:type" content="profile" />
          <meta property="og:profile:id" content={userId} />
          <meta property="og:profile:username" content={username} />
          <meta property="og:image" content={userPicture.href} />
          <meta property="og:image:width" content="512" />
          <meta property="og:image:height" content="512" />
          <meta property="twitter:image" content={userPicture.href} />
        </Helmet>
        <UserProfileJsonLd userId={userId} type={listType} />
      </>
    );
  }

  componentDidMount() {
    const { isFirstPageFetched, isFirstPageFetching } = this.props;
    const shouldFetchFirstPage = !(isFirstPageFetched || isFirstPageFetching);
    if (window.__IS_HYDRATED__ || shouldFetchFirstPage) {
      // don't need to hydrate since SSR data is empty.
      // mostly for local dev env.
      window.__IS_HYDRATED__ = 'Profile';
    }
  }
  componentWillUnmount() {
    this.timeout && clearTimeout(this.timeout);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'Profile';
    }
  }
  render() {
    const { userId, listType, isMeProfile } = this.props;
    return (
      <StyledProfile
        shouldPadFooter={getIsIphoneXSeriesPWA()}
        shouldShowBottomNavigation={isMeProfile}
      >
        {!isMeProfile && this.renderHelmet()}
        <PullToRefresh
          useWindow
          onRefresh={async ({ reset }) => {
            await this.handleRefresh();
            return reset();
          }}
        >
          <Body>
            <>
              {this.renderProfileInfo()}
              <ProfileTab
                listType={listType}
                userId={userId}
                isMeProfile={isMeProfile}
              />
              {this.renderTabContent()}
            </>
          </Body>
        </PullToRefresh>
        <ModalView
          id="ProfileInfoEditor"
          noScrollLock
          render={() => <ProfileInfoEditor />}
        />
        <ModalView
          id="ProfileCoverPhotoUploaderModal"
          render={() => (
            <UniversalContainer filename="ProfileCoverPhotoUploader" />
          )}
        />
        <ModalView
          id="ProfileAvatarPhotoUploaderModal"
          render={() => (
            <UniversalContainer filename="ProfileAvatarPhotoUploader" />
          )}
        />
        <ModalView
          id="DraftsDropAlert"
          render={() => <UniversalContainer filename="DraftsDrop" />}
        />
        <ModalView
          id="ProfileMoreModal"
          render={() => <UniversalContainer filename="ProfileMoreModal" />}
        />
        <ModalView
          id="UnfollowConfirmModal"
          render={() => (
            <UniversalContainer
              filename="UnfollowConfirmModal"
              userId={userId}
            />
          )}
        />
      </StyledProfile>
    );
  }
}

Profile.propTypes = {
  intersectedFeedName: PropTypes.string,
  postIntersectedFeedName: PropTypes.string,
  meId: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
  biography: PropTypes.string,
  shareLink: PropTypes.string,
  shortShareLink: PropTypes.string,
  listType: PropTypes.string,
  defaultSubscriptionCurrency: PropTypes.string,
  profileFeedUnixTimestamp: PropTypes.number,
  isMeProfile: PropTypes.bool,
  userDetailFetchStatus: PropTypes.object,
  isFirstPageFetched: PropTypes.bool,
  isFirstPageFetching: PropTypes.bool,
  filters: PropTypes.array,
  sorts: PropTypes.array,
  postFilters: PropTypes.array,
  postSorts: PropTypes.array,
  staticContext: PropTypes.object,
  t: PropTypes.func.isRequired,
  goBack: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchFollowingTotalCount: PropTypes.func,
  fetchFollowersTotalCount: PropTypes.func,
  fetchUserFollowersTotalCount: PropTypes.func,
  fetchUserFollowingTotalCount: PropTypes.func,
  fetchUserIsFollowing: PropTypes.func,
  fetchUserIsBlocked: PropTypes.func,
  fetchShortLink: PropTypes.func,
  fetchMeCreator: PropTypes.func,
  fetchDiamondPacks: PropTypes.func,
  fetchUserSubscription: PropTypes.func,
  fetchFeeds: PropTypes.func,
  fetchFeedsTotalCount: PropTypes.func,
  subscribeOnlineStatus: PropTypes.func,
  updateProfileFeedUnixTimestamp: PropTypes.func,
};

Profile.defaultProps = {
  intersectedFeedName: null,
  postIntersectedFeedName: null,
  meId: null,
  userId: null,
  username: null,
  biography: null,
  shareLink: null,
  shortShareLink: null,
  listType: null,
  defaultSubscriptionCurrency: null,
  profileFeedUnixTimestamp: null,
  isMeProfile: false,
  userDetailFetchStatus: {},
  isFirstPageFetched: false,
  isFirstPageFetching: false,
  filters: [],
  sorts: [],
  postFilters: [],
  postSorts: [],
  staticContext: {},
  goBack: () => null,
  fetchUser: () => null,
  fetchFollowingTotalCount: () => null,
  fetchFollowersTotalCount: () => null,
  fetchUserFollowersTotalCount: () => null,
  fetchUserFollowingTotalCount: () => null,
  fetchUserIsFollowing: () => null,
  fetchUserIsBlocked: () => null,
  fetchShortLink: () => null,
  fetchMeCreator: () => null,
  fetchDiamondPacks: () => null,
  fetchUserSubscription: () => null,
  fetchFeeds: () => null,
  fetchFeedsTotalCount: () => null,
  subscribeOnlineStatus: () => null,
  updateProfileFeedUnixTimestamp: () => null,
};

const StyledProfile = styled.div`
  padding-top: ${headerHeight}px;
  padding-bottom: ${({ shouldPadFooter, shouldShowBottomNavigation }) =>
    (shouldPadFooter ? iPhoneXSeriesFooterPaddingInPWA : 0) +
    (shouldShowBottomNavigation ? footerNavigationHeight + 20 : 0)}px;
`;

const Body = styled.main`
  margin: 0 auto;
  padding-bottom: 100px;
  border-left: 1px solid rgba(100, 100, 100, 0.15);
  border-right: 1px solid rgba(100, 100, 100, 0.15);
  width: 100%;
  max-width: 960px;
  min-height: 100vh;
  ${media.mobile`
    border-left: unset;
    border-right: unset;
    max-width: none;
  `};
  ${media.tablet`
    max-width: 680px;
  `};
`;

const ProfileInfoWrapper = styled.div`
  padding: 20px 20px 8px 40px;
  background-color: ${color.black};
  ${media.mobile`
    padding: 16px 16px 8px;
  `};
`;

const ListWrapper = styled.div``;

export default withTranslation()(Profile);
